import { useMemo, useState, useEffect, useCallback } from 'react'
import { numberFormat, round } from 'shared/util'

import { useSearch } from 'features/search/context'

import { SenmoniStaticticsRecord } from './data'

import { ListRecordWithBar } from 'components/controls/listRecordWithBar'

import {
  Stack,
  Box,
  BoxProps,
  Typography,
  Alert,
  List,
  Button,
} from '@mui/material'

import { SisetuDoctorsConditions } from '../condition'

const INITIAL_SHOW_COUNT = 9

type SenmoniListSectionProps = {
  sisetuCode: string
  dataList?: SenmoniStaticticsRecord[]
  condition: SisetuDoctorsConditions
  onChangeCondition: (axis: 'senmoni', condition: { code: string, name: string }) => void
} & BoxProps
export const SenmoniListSection = ({
  sisetuCode,
  dataList,
  condition,
  onChangeCondition,
  ...others
}: SenmoniListSectionProps) => {
  const search = useSearch()
  const [showAll, setShowAll] = useState<boolean>(false)

  const isTarget = useCallback(
    (r: SenmoniStaticticsRecord) => {
      if (!search?.senmoni) return false
      return search.senmoni.some((s) => s.senmoniCode === r.senmoniCode)
    },
    [search]
  )
  const summary = useMemo<{
    target: SenmoniStaticticsRecord[]
    list: SenmoniStaticticsRecord[]
    total: number
    max: number
  } | null>(() => {
    if (!dataList) return null

    const newDataList = dataList
    newDataList.sort((a, b) => b.kojinCount - a.kojinCount)

    let total = 0
    let max = 0
    for (const record of newDataList) {
      total += record.kojinCount
      max = Math.max(record.kojinCount, max)
    }
    return {
      target: newDataList.filter((r) => isTarget(r)),
      list: newDataList.filter((r) => !isTarget(r)),
      total: total,
      max: max,
    }
  }, [dataList, isTarget])

  useEffect(() => setShowAll(false), [sisetuCode])

  return (
    <Box {...others} sx={{ height: '100%' }}>
      <Stack direction="column" sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          paddingX={1}
          paddingY={1}
          spacing={0.5}
        >
          <Typography
            variant="body1"
            component="div"
            color="text.primary"
            sx={{ textAlign: 'left', flexGrow: 1 }}
          >
            専門医
          </Typography>
          {summary ? (
            <Typography
              variant="body2"
              component="div"
              color="text.secondary"
              sx={{ textAlign: 'right' }}
            >
              全 {numberFormat(summary.list.length)} 種 / 延べ{' '}
              {numberFormat(round(summary.total, 1))} 人
            </Typography>
          ) : (
            <></>
          )}
        </Stack>

        {summary ? (
          <Stack
            direction="column"
            spacing={1}
            sx={{
              flexGrow: 1,
              overflow: 'auto',
            }}
          >
            {summary.target.length > 0 ? (
              <List disablePadding>
                {summary.target.map((r) => (
                  <ListRecordWithBar
                    key={`senmoni_${r.senmoniCode}`}
                    isTarget
                    index={0}
                    selected={condition.senmoni?.code === r.senmoniCode}
                    code={r.senmoniCode}
                    name={r.senmoniName}
                    count={r.kojinCount}
                    max={summary.max}
                    onClicked={(code, name) => onChangeCondition('senmoni', { code, name })}
                  />
                ))}
              </List>
            ) : (
              <></>
            )}

            {summary.list.length > 0 ? (
              <List disablePadding>
                {(showAll
                  ? summary.list
                  : summary.list.slice(0, INITIAL_SHOW_COUNT)
                ).map((r, i) => (
                  <ListRecordWithBar
                    key={`senmoni_${r.senmoniCode}`}
                    index={i}
                    selected={condition.senmoni?.code === r.senmoniCode}
                    code={r.senmoniCode}
                    name={r.senmoniName}
                    count={r.kojinCount}
                    max={summary.max}
                    onClicked={(code, name) => onChangeCondition('senmoni', { code, name })}
                  />
                ))}
                {!showAll && summary.list.length > INITIAL_SHOW_COUNT && (
                  <Button fullWidth onClick={() => setShowAll(true)}>
                    すべて表示
                  </Button>
                )}
              </List>
            ) : (
              <Box padding={1}>
                <Alert severity="info">対象が見つかりません.</Alert>
              </Box>
            )}
          </Stack>
        ) : (
          <></>
        )}
      </Stack>
    </Box>
  )
}
